import { inject, Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { CatchErrorService } from '@core/catch-error.service';
import { BehaviorSubject, from, Observable, of,  throwError } from "rxjs";
import { catchError, map, shareReplay, switchMap } from "rxjs/operators";
import { collection, doc, DocumentData, Firestore, getDoc, getDocs, onSnapshot, orderBy, query, QuerySnapshot, where } from "@angular/fire/firestore";
import { UserManagementService } from 'src/app/services/user-management.service';
import { CommonService } from 'src/app/services/common/common.service';

@Injectable({
  providedIn: 'root'
})
export class GameCelebrationService {

    constructor(
        private authService: AuthService,
        private userManagementService: UserManagementService,
        private catchService: CatchErrorService,
        private firestore: Firestore,
    ) {}
    private commonService = inject(CommonService);

    showToken$ = new BehaviorSubject<Data>([]);

  // ============================== Celebration documents ======================//
  // =============================XPS animation Script related document ================================ //
  // This method has been use to get the Xps value from the xp details document
  getXpsDetailsData$ = this.authService.getUid$("SyndicateRepositoryService").pipe(
    switchMap(userId => {
      if (!userId) {
        return of(null); // or EMPTY if you want no emission
      }
      return this.authService
        .getAngularFireStore(
          `users/${userId}/gamification/celebrations/xp/details`,
          "snapshotChanges"
        )
        .pipe(
          map((res: Data) => {
            const data = res?.payload?.data();
            return data;
          })
        );
    })
  );


  // This method has been use to get the current_celebration document
  getCurrentCelebrationData() {
    return this.authService.getUid$("GamesRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);
        return this.authService
          .getAngularFireStore(
            `users/${userId}/gamification/current_celebration`,
            "snapshotChanges"
          )
          .pipe(
            map((res: Data) => {
              const data = res?.payload?.data();
              return data;
            })
          );
      })
    );
  }


  // This method has been use to update the presented = true node in the Xps document
  updateXpsDocument() {
    try {
      const userId = this.authService.getUid$("GamesRepositoryService").pipe(
        switchMap((userId) => {
          return of(userId) || of(null);
        }));

      const path_id = "IKQkR11TFcK9YcAouZes";
      const doc_data = { presented: true };
      const method = "update";

      this.authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe({
        error: (err) => {
          return err;
        }
      });
    } catch (error) {
      return throwError(error);
    }
  }


  updateCurrentCelebrationDocument() {
    try {
      const userId = this.authService.getUid$("GamesRepositoryService").pipe(
        switchMap((userId) => {
          return of(userId) || of(null);
        }));
      const path_id = "vHA2zOEypdLBuxVt9kRL";
      const doc_data = { processed_status: true };
      const method = "update";

      this.authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe({
        error: (err) => {
          return err;
        }
      });
    } catch (error) {
      return throwError(error);
    }
  }

   //Check token animation value
  checkTokenAnimation() {
    this.getTokenDetails$?.subscribe({
      next: (ref: Data) => {
        this.showToken$.next(ref);
      },
      error: () => { }
    });
  }

    // Prototype Games

  // get token details for display animation
  getTokenDetails$ = this.userManagementService?.userRequest$.pipe(
    switchMap(() =>
      this.authService.getUid$("GamesRepositoryService").pipe(
        switchMap((userId) => {
          return userId
            ? this.authService
              .getAngularFireStore(`/users/${userId}/balance/rapid_fire_token_animation`, "get")
              .pipe(
                map((res: Data) => res?.data()),
                shareReplay(1)
              )
            : of([]);
        })
      )
    )
  );

  // This is method has been used to get Rank Details Data on it.
  getRankDetailsData() {
    return this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) return of([]);
          return this.authService.getUid$("GamesRepositoryService").pipe(
            switchMap(async (userId) => {
              if (!userId) return [];
              // Modular Firestore API: Reference the collection and query
              const rankCollectionRef = collection(
                this.firestore,
                `users/${userId}/gamification/celebrations/rank`
              );
              const rankQuery = query(rankCollectionRef, where("presented", "==", false));

              // Fetch documents matching the query
              const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(rankQuery);
              const documents = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                data: doc.data(),
              }));
              return documents;
            })
          );
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .pipe(
        map((res: any[]) => res),
        catchError(() => {
          return of([]);
        })
      );
  }

   // This method has been update to get Rank Details Data on it.
  updateRankDetailsData(docID: string) {
    try {
      const userId = this.authService.getUid$("GamesRepositoryService").pipe(
        switchMap((userId) => {
          return of(userId) || of(null);
        }));

      const path_id = "O4e87Y2tOUPuMAW1GKmN";
      const doc_data = { presented: true };
      const method = "update";

      this.authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe({
        error: () => {
          return false;
        }
      });
    } catch (error) {
      return throwError(error);
    }
  }

   // This method has been used to get current VIP Rank Details Data on it.
  getVipStatusDetailsData() {
    return this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) return of([]);
          return this.authService.getUid$("GamesRepositoryService").pipe(
            switchMap(async (userId) => {
              if (!userId) return [];
              // Modular Firestore API: Reference the collection and query
              const vipStatusCollectionRef = collection(
                this.firestore,
                `users/${userId}/gamification/celebrations/rank_vip_status`
              );
              const vipStatusQuery = query(vipStatusCollectionRef, where("presented", "==", false));

              // Fetch documents matching the query
              const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(vipStatusQuery);
              const documents = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                data: doc.data(),
              }));
              return documents;
            })
          );
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .pipe(
        map((res: any[]) => res),
        catchError(() => {
          return of([]);
        })
      );
  }

  // This method has been used to update Presented true node in rank_vip_status document
  updateVipStatusDetailsData(currentVipStatusRank: string) {
    if (currentVipStatusRank !== null && currentVipStatusRank !== undefined) {
      try {
        const userId = this.authService.getUid$("GamesRepositoryService").pipe(
          switchMap((userId) => {
            return of(userId) || of(null);
          }));

        const path_id = "bZ4QfZigTAtbGzEy04Jt";
        const doc_data = { presented: true };
        const method = "update";

        this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: () => {
            return false;
          }
        });
      } catch (error) {
        return throwError(error);
      }
    } else {
      return throwError("No Rank Found");
    }
  }

  // This method has been used to get Rank Details Data on it.
  getVipStatusLevelUpData() {
    return this.authService.getAngularFireStore("/Global_lookup/vip_status_level_up", "snapshotChanges").pipe(
      map((res: Data) => {
        const data = res?.payload?.data();
        return data;
      })
    );
  }


  // ======================== Achievement celebration documents ================================ //
  //=======================This function is used to get Goals Current Data ======================
  getGoalsCurrentData$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return of([]);

      // 🔑 Wait for userId before building collection path
      return this.commonService.getUid("GamesRepositoryService").pipe(
        switchMap((userId) => {
          if (!userId) return of([]);


          const goalsCollectionRef = collection(
            this.firestore,
            `users/${userId}/gamification/goals/current`
          );

          const goalsQuery = query(
            goalsCollectionRef,
            where("settings.presented", "==", false),
            orderBy("update_timestamp", "desc")
          );

          return new Observable((subscriber) => {
            const unsubscribe = onSnapshot(
              goalsQuery,
              (snapshot) => {
                const actions = snapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                }));
                subscriber.next(actions);
              },
              (error) => subscriber.error(error)
            );
            return () => unsubscribe();
          }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
        })
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1),
    map((res: Data) => res)
  );


    //This function using to get badge details data to show achievement animation
  getBadgesDetailsData$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return of([]);

      // ✅ Use commonService to get UID
      return this.commonService.getUid("GamesRepositoryService").pipe(
        switchMap((userId) => {
          if (!userId) return of([]); // fallback if no userId

          // Modular Firestore API: Reference the collection
          const badgesDetailsCollectionRef = collection(
            this.firestore,
            `users/${userId}/gamification/badges/details`
          );

          // Query with filters
          const badgesQuery = query(
            badgesDetailsCollectionRef,
            where("settings.presented", "==", false)
          );

          // Use onSnapshot for real-time updates
          return new Observable((subscriber) => {
            const unsubscribe = onSnapshot(
              badgesQuery,
              (snapshot) => {
                const actions = snapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                }));
                subscriber.next(actions);
              },
              (error) => {
                subscriber.error(error);
              }
            );
            return () => unsubscribe();
          }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
        })
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1),
    map((res: Data) => res)
  );

   // This method has been used to get badge_look up list Data on it.
  getBadgeLookupData(docRef: string) {
    // Modular Firestore API: Reference the document
    const badgeDocRef = doc(this.firestore, `${docRef}`);

    // Convert the promise-based `getDoc` into an observable using `from`
    return from(getDoc(badgeDocRef)).pipe(
      map((documentSnapshot) => documentSnapshot.data()), // Process the document data
      catchError((error) => {
        throw error; // Rethrow the error
      })
    );
  }


  // =================== Update Achievement documents ===============================//
  // This method has been update to get Goals Current documents
  async updateGoalsCurrentData(docID: string) {
    try {
      const userId = this.authService.getUid$("GamesRepositoryService").pipe(
        switchMap((userId) => {
          return of(userId) || of(null);
        }));

      const path_id = "szZ4QKc4xm1F2Qrij7bq";
      const doc_data = { settings: { presented: true } };
      const method = "update";

      return await this.authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe({
        error: () => {
          return false;
        }
      });
    } catch (err) {
      return err;
    }
  }

    // This method has been update to get badge details documents
  async updateBadgesCurrentData(docID: string) {
    try {
      const userId = this.authService.getUid$("GamesRepositoryService").pipe(
        switchMap((userId) => {
          return of(userId) || of(null);
        }));

      const path_id = "nYXdRsZFlca3zOFduCUW";
      const doc_data = { settings: { presented: true } };
      const method = "update";

      return await this.authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      }).subscribe({
        error: (error) => {
          return false;
        }
      });
    } catch (err) {
      return err;
    }
  }
}